
import React, { useState, useEffect, useCallback } from 'react';
import { MdCheckCircle, MdInfo, MdVisibility, MdArrowBack, MdSearch, MdMoreVert, MdAdd, MdClose, MdPrint } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import {
    GET_VENDOR_REQUEST_DATA_API,
    GET_PENDING_REQUESTS_API,
    GET_ALL_RES_RECEIVED_REQ_API,
    GET_COMPLETED_RESPONSE_REQ_API,
    SEND_WHATSAPP_LINK_API,
    VIEW_PURCHASE_ORDER_API,
    GET_REMAINING_VENDOR_LIST_API,
    ADD_VENDOR_TO_REQUEST_API
} from '../config/API';
import './CompareGenerateRequest.css';
import '../pages/Dashboard.css';
import './ViewVendorQuotations.css';
import Loader from '../components/common/Loader';
import { formatDate } from '../utils/dateUtils';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import { useUser } from '../context/UserContext';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import Modal from '../components/common/Modal';
import { encryptToken } from '../utils/secureToken';

const ViewVendorQuotations = () => {
    const navigate = useNavigate();
    const { hasPermission } = useUser();

    // STATE: activeTab for filtering POs
    const [activeTab, setActiveTab] = useState('response'); // Default to response
    const [allPOs, setAllPOs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [responseSearch, setResponseSearch] = useState('');
    const [completedSearch, setCompletedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const searchTerm = '';

    // Action Menu State
    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);

    // WhatsApp Share State
    const [showWhatsappModal, setShowWhatsappModal] = useState(false);
    const [selectedVendorsForWhatsapp, setSelectedVendorsForWhatsapp] = useState([]);
    const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
    const [selectedPoForWhatsapp, setSelectedPoForWhatsapp] = useState(null);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [whatsappMessage, setWhatsappMessage] = useState("please click on this link to update rate");

    // Add Vendor State
    const [showAddVendorModal, setShowAddVendorModal] = useState(false);
    const [selectedPoForAddVendor, setSelectedPoForAddVendor] = useState(null);
    const [remainingVendors, setRemainingVendors] = useState([]);
    const [selectedVendorForAdd, setSelectedVendorForAdd] = useState(null);
    const [loadingRemainingVendors, setLoadingRemainingVendors] = useState(false);
    const [addingVendor, setAddingVendor] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };


    const fetchQuotationData = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const apiToCall =
                activeTab === 'response' ? GET_COMPLETED_RESPONSE_REQ_API :
                    activeTab === 'completed' ? GET_ALL_RES_RECEIVED_REQ_API :
                        GET_VENDOR_REQUEST_DATA_API;
            
            const currentSearchTerm = activeTab === 'response' ? responseSearch : activeTab === 'completed' ? completedSearch : '';

            const response = await axios.get(apiToCall, {
                params: {
                    page: currentPage,
                    limit: limit,
                    search: currentSearchTerm
                },
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.status) {
                const rawData = response.data.data || [];

                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.totalPages);
                    setTotalRecords(response.data.pagination.total);
                } else if (response.data.totalRecords !== undefined) {
                    setTotalPages(Math.ceil(response.data.totalRecords / 10));
                    setTotalRecords(response.data.totalRecords);
                } else {
                    // Fallback
                    setTotalPages(1);
                    setTotalRecords(rawData.length);
                }

                // Standardize mapping for the list view
                const formattedData = rawData.map(item => {
                    // Normalize the request object
                    const req = item.RequestVendor?.Request || item;
                    const vendors = item.RequestVendors || (item.RequestVendor?.Vendor ? [item.RequestVendor.Vendor] : []);

                    // Unique vendors count
                    const vendorIds = new Set();
                    if (item.RequestVendors) {
                        item.RequestVendors.forEach(rv => { if (rv.vendorId) vendorIds.add(rv.vendorId); });
                    } else if (item.RequestVendor?.vendorId) {
                        vendorIds.add(item.RequestVendor.vendorId);
                    } else if (item.vendorId) {
                        vendorIds.add(item.vendorId);
                    }

                    let vendorName = '-';
                    if (vendors.length > 1) {
                        vendorName = `${vendors.length} Vendors`;
                    } else if (vendors.length === 1) {
                        vendorName = vendors[0].vendorName || vendors[0].Vendor?.vendorName || '-';
                    }

                    return {
                        id: req.id || item.reqId || item.requestId || item.id,
                        requestNumber: req.reqNumber || item.reqNumber || '-',
                        grandTotal: item.grandTotal || req.grandTotal || '0.00',
                        date: item.date || req.date || req.tillDate || item.createdAt || req.createdAt,
                        status: activeTab === 'completed' ? 'completed' : (item.status || req.status || 'pending'),
                        vendorName: vendorName,
                        projectName: req.projectName || req.ProjectMaster?.projectName || req.Project?.projectName || '-',
                        vendorCount: vendorIds.size || vendors.length || 0
                    };
                });

                setAllPOs(formattedData);
            }
        } catch (error) {
            console.error("Error fetching quotation data:", error);
        } finally {
            setLoading(false);
        }
    }, [activeTab, currentPage, responseSearch, completedSearch, limit]);

    useEffect(() => {
        fetchQuotationData();
    }, [fetchQuotationData]);

    // Filter Logic
    const displayedPOs = allPOs.filter(po => {
        const status = (po.status || '').toLowerCase();

        // Tab match logic
        let isMatch = false;
        if (activeTab === 'completed') {
            isMatch = (status === 'completed');
        } else {
            isMatch = status.includes('response');
        }

        if (!isMatch) return false;

        // Search terminology
        const searchTerm = (activeTab === 'response' ? responseSearch : activeTab === 'completed' ? completedSearch : '').toLowerCase();
        if (!searchTerm) return true;

        return (
            (po.requestNumber || '').toLowerCase().includes(searchTerm) ||
            (po.vendorName || '').toLowerCase().includes(searchTerm) ||
            (po.projectName || '').toLowerCase().includes(searchTerm)
        );
    });

    // HANDLERS
    const handlePOSelect = (id) => {
        navigate(`/vendor-quotes/${id}`);
    };

    const handlePrintQuotation = (id) => {
        setOpenMenuId(null);
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.src = `/print-quotation/${id}`;
        document.body.appendChild(iframe);
        
        // Clean up the iframe after a minute to avoid memory leaks
        setTimeout(() => {
            if (document.body.contains(iframe)) {
                document.body.removeChild(iframe);
            }
        }, 60000);
    };

    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.swal2-container')) return;
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;

            setOpenMenuId(null);
            setAnchorRect(null);
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    const openWhatsappModal = async (po) => {
        setOpenMenuId(null);
        setSelectedPoForWhatsapp(po);
        setShowWhatsappModal(true);
        setLoadingVendors(true);

        try {
            const token = localStorage.getItem('accessToken');

            // Fetch full PO details to ensure we have all vendor info
            const poResponse = await axios.get(VIEW_PURCHASE_ORDER_API(po.id), {
                headers: { Authorization: `Bearer ${token}` }
            });

            const requestVendors = poResponse.data.data.RequestVendors || poResponse.data.data.PurchaseOrderVendors || [];
            const uniqueVendors = requestVendors.length
                ? [...new Map(requestVendors.map(v => [v.Vendor?.id || v.Vendor?._id, v])).values()]
                : [];

            const formattedVendors = uniqueVendors.map(v => {
                const vendorData = v.Vendor || {};
                const mobileNumber = vendorData.whatsappNumber ||
                    vendorData.whatsAppNumber ||
                    vendorData.whatsapp_number ||
                    vendorData.contactNumber ||
                    vendorData.mobileNumber || '';

                return {
                    vendorId: vendorData.id || v.vendorId,
                    vendorName: vendorData.vendorName || 'Unknown Vendor',
                    mobileNumber: mobileNumber,
                    isChecked: true
                };
            });

            setSelectedVendorsForWhatsapp(formattedVendors);
        } catch (error) {
            console.error('Error fetching details for WhatsApp modal:', error);
            Swal.fire('Error', 'Failed to load vendor details.', 'error');
        } finally {
            setLoadingVendors(false);
        }
    };

    const handleWhatsappCheckboxChange = (vendorId) => {
        setSelectedVendorsForWhatsapp(prev => prev.map(v =>
            v.vendorId === vendorId ? { ...v, isChecked: !v.isChecked } : v
        ));
    };

    const handleSendWhatsapp = async () => {
        const vendorsToSend = selectedVendorsForWhatsapp.filter(v => v.isChecked);
        if (vendorsToSend.length === 0) {
            Swal.fire('Error', 'Please select at least one vendor.', 'error');
            return;
        }

        const token = localStorage.getItem('accessToken');
        setSendingWhatsapp(true);
        let successCount = 0;
        let failCount = 0;

        const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

        for (let i = 0; i < vendorsToSend.length; i++) {
            const vendor = vendorsToSend[i];
            if (!vendor.mobileNumber) { failCount++; continue; }

            if (i > 0) await sleep(2000);

            try {
                const encryptedRequestId = encryptToken(Number(selectedPoForWhatsapp.id));
                const encryptedVendorId = encryptToken(Number(vendor.vendorId));
                const portalLink = `${window.location.origin}/vendor-portal/${encryptedRequestId}/${encryptedVendorId}`;

                const payload = {
                    vendorId: vendor.vendorId,
                    message: `${whatsappMessage}\n\n${portalLink}`,
                    link: portalLink
                };

                const response = await axios.post(SEND_WHATSAPP_LINK_API(selectedPoForWhatsapp.id), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const resDataArr = response.data?.data;
                const internalError = Array.isArray(resDataArr) && resDataArr.length > 0 && resDataArr[0].error;

                if (internalError) {
                    failCount++;
                } else {
                    successCount++;
                }
            } catch (error) {
                failCount++;
            }
        }

        if (successCount > 0) {
            try {
                await axios.post(
                    SEND_WHATSAPP_LINK_API(selectedPoForWhatsapp.id),
                    { message: "ignore_this_message" },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (error) {
                console.error('Failed to send admin notification:', error);
            }
        }

        setSendingWhatsapp(false);
        setShowWhatsappModal(false);

        if (successCount > 0 && failCount === 0) {
            Swal.fire('Success', `WhatsApp message sent to ${successCount} vendor(s).`, 'success');
        } else if (successCount > 0 && failCount > 0) {
            Swal.fire('Partial Success', `Sent to ${successCount} vendor(s), failed for ${failCount}.`, 'warning');
        } else {
            Swal.fire('Error', 'Failed to send WhatsApp messages.', 'error');
        }
    };

    const openAddVendorModal = async (po) => {
        setOpenMenuId(null);
        setSelectedPoForAddVendor(po);
        setSelectedVendorForAdd(null);
        setShowAddVendorModal(true);
        setLoadingRemainingVendors(true);

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_REMAINING_VENDOR_LIST_API(po.id), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                const vendors = response.data.remainingVendorList || [];
                const formattedOptions = vendors.map(v => ({
                    value: v.id,
                    label: v.companyName ? `${v.vendorName} (${v.companyName})` : v.vendorName,
                    vendorName: v.vendorName,
                    companyName: v.companyName
                }));
                setRemainingVendors(formattedOptions);
            } else {
                setRemainingVendors([]);
            }
        } catch (error) {
            console.error('Error fetching remaining vendors:', error);
            Swal.fire('Error', 'Failed to load available vendors.', 'error');
        } finally {
            setLoadingRemainingVendors(false);
        }
    };

    const handleAddVendorSubmit = async () => {
        if (!selectedVendorForAdd) {
            Swal.fire('Error', 'Please select a vendor.', 'error');
            return;
        }

        setAddingVendor(true);
        try {
            const token = localStorage.getItem('accessToken');
            const reqId = selectedPoForAddVendor.id;
            const vendorId = selectedVendorForAdd.value;

            const encryptedRequestId = encryptToken(Number(reqId));
            const encryptedVendorId = encryptToken(Number(vendorId));
            const portalLink = `${window.location.origin}/vendor-portal/${encryptedRequestId}/${encryptedVendorId}`;

            const payload = {
                reqId: Number(reqId),
                vendorId: Number(vendorId),
                link: portalLink
            };

            const response = await axios.post(ADD_VENDOR_TO_REQUEST_API, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                Swal.fire('Success', 'Vendor added to request successfully.', 'success');
                setShowAddVendorModal(false);
                fetchQuotationData();
            } else {
                Swal.fire('Error', response.data.message || 'Failed to add vendor.', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'An error occurred while adding vendor.', 'error');
        } finally {
            setAddingVendor(false);
        }
    };

    return (
        <>
        <div className="dashboard compare-po-page">

            <h2 className="section-title">
                View Vendor Quotation
            </h2>

            {/* <div className="vendors-quotation-container"> */}
            {/* LEVEL 1: PO LIST TABLE */}
            <div className={isMobile ? "" : "card-container-vendors-quotation fade-in"}>

                {/* Tab-wise Search Bar */}
                {(activeTab === 'response' || activeTab === 'completed') && (
                    <div className="search-container">
                        <TableLimitSelector limit={limit} onLimitChange={handleLimitChange} />
                        <div className="search-wrapper">
                            <MdSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder={`Search ${activeTab} quotations by number, project or vendor...`}
                                className="search-input"
                                value={activeTab === 'response' ? responseSearch : completedSearch}
                                onChange={(e) => {
                                    if (activeTab === 'response') {
                                        setResponseSearch(e.target.value);
                                    } else {
                                        setCompletedSearch(e.target.value);
                                    }
                                    setCurrentPage(1);
                                }}
                            />
                            {(activeTab === 'response' ? responseSearch : completedSearch) && (
                                <button
                                    className="clear-search-btn"
                                    onClick={() => activeTab === 'response' ? setResponseSearch('') : setCompletedSearch('')}
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className={isMobile ? "tabs-card" : ""} style={{ 
                    padding: '1rem', 
                    borderBottom: isMobile ? 'none' : '1px solid #e5e7eb', 
                    display: 'flex', 
                    gap: '1rem', 
                    flexWrap: 'wrap' 
                }}>
                    <button
                        className={`tab-btn ${activeTab === 'response' ? 'active-tab' : ''}`}
                        onClick={() => { setActiveTab('response'); setCurrentPage(1); }}
                        style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            background: activeTab === 'response' ? '#eef2ff' : 'transparent',
                            color: activeTab === 'response' ? '#4f46e5' : '#6b7280',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            flex: isMobile ? 1 : 'none'
                        }}
                    >
                        Response
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'completed' ? 'active-tab' : ''}`}
                        onClick={() => { setActiveTab('completed'); setCurrentPage(1); }}
                        style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            background: activeTab === 'completed' ? '#ecfdf5' : 'transparent',
                            color: activeTab === 'completed' ? '#059669' : '#6b7280',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            flex: isMobile ? 1 : 'none'
                        }}
                    >
                        Completed Response
                    </button>
                </div>

                {loading ? (
                    <Loader text="Fetching quotations..." />
                ) : (
                    <>
                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Request Number</th>
                                        <th>Vendor</th>
                                        <th>Project</th>
                                        <th>Till Date</th>
                                        <th>Status</th>

                                        {/* <th style={{ textAlign: 'right' }}>Total</th> */}
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedPOs.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                                                No {activeTab} quotations found
                                            </td>
                                        </tr>
                                    ) : (
                                        displayedPOs.map((po) => (
                                            <tr key={po.id}>
                                                <td><span className="po-number" style={{ fontFamily: 'monospace' }}>{po.requestNumber?.toUpperCase()}</span></td>
                                                <td >
                                                    <span className="vendor-count-badge">
                                                        {po.vendorCount} Vendors
                                                    </span>
                                                </td>
                                                <td>{po.projectName}</td>
                                                <td>{formatDate(po.date)}</td>
                                                <td>
                                                    <span className={`status-badge status-${(po.status || 'pending').toLowerCase()}`}>
                                                        {po.status}
                                                    </span>
                                                </td>

                                                {/* <td style={{ textAlign: 'right' }}>
                                                        ₹{po.grandTotal.toLocaleString()}
                                                    </td> */}
                                                <td style={{ textAlign: 'right' }} className={`action-cell ${openMenuId === po.id ? 'menu-open' : ''}`}>
                                                    <button
                                                        className="action-menu-btn"
                                                        onClick={(e) => toggleMenu(po.id, e)}
                                                        title="Actions"
                                                    >
                                                        <MdMoreVert size={20} />
                                                    </button>

                                                    <ActionMenuPortal
                                                        isOpen={openMenuId === po.id}
                                                        anchorRect={anchorRect}
                                                        onClose={() => setOpenMenuId(null)}
                                                    >
                                                        <div className="action-dropdown">
                                                            {hasPermission('VIEW_QUOTATIONS', 'read') && (
                                                                <button className="action-item" onClick={() => handlePOSelect(po.id)}>
                                                                    <MdVisibility size={16} /> View Quotations
                                                                </button>
                                                            )}
                                                            <button className="action-item" onClick={() => openWhatsappModal(po)}>
                                                                <FaWhatsapp size={16} /> Share on WhatsApp
                                                            </button>
                                                            {(activeTab === 'response' || activeTab === 'completed') && (
                                                                <button className="action-item" onClick={() => openAddVendorModal(po)}>
                                                                    <MdAdd size={16} /> Add Vendor
                                                                </button>
                                                            )}
                                                            {activeTab === 'completed' && (
                                                                <button className="action-item" onClick={() => handlePrintQuotation(po.id)}>
                                                                    <MdPrint size={16} /> Print Quotation
                                                                </button>
                                                            )}
                                                        </div>
                                                    </ActionMenuPortal>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View (Visible only on small screens) */}
                        <div className="mobile-card-view">
                            {displayedPOs.length === 0 ? (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                                    No {activeTab} quotations found
                                </div>
                            ) : (
                                displayedPOs.map((po) => (
                                    <div key={po.id} className="dashboard-card">
                                        <div className="card-header">
                                            <strong className="po-number">{po.requestNumber?.toUpperCase()}</strong>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <span className={`status-badge status-${(po.status || 'pending').toLowerCase()}`}>
                                                    {po.status}
                                                </span>
                                                <button
                                                    className="action-menu-btn"
                                                    onClick={(e) => toggleMenu(po.id, e)}
                                                    title="Actions"
                                                >
                                                    <MdMoreVert size={20} />
                                                </button>
                                            </div>

                                            <ActionMenuPortal
                                                isOpen={openMenuId === po.id}
                                                anchorRect={anchorRect}
                                                onClose={() => setOpenMenuId(null)}
                                            >
                                                <div className="action-dropdown shadow-lg">
                                                    {hasPermission('VIEW_QUOTATIONS', 'read') && (
                                                        <button className="action-item" onClick={() => handlePOSelect(po.id)}>
                                                            <MdVisibility size={16} /> View Quotations
                                                        </button>
                                                    )}
                                                    <button className="action-item" onClick={() => openWhatsappModal(po)}>
                                                        <FaWhatsapp size={16} /> Share on WhatsApp
                                                    </button>
                                                    {(activeTab === 'response' || activeTab === 'completed') && (
                                                        <button className="action-item" onClick={() => openAddVendorModal(po)}>
                                                            <MdAdd size={16} /> Add Vendor
                                                        </button>
                                                    )}
                                                    {activeTab === 'completed' && (
                                                        <button className="action-item" onClick={() => handlePrintQuotation(po.id)}>
                                                            <MdPrint size={16} /> Print Quotation
                                                        </button>
                                                    )}
                                                </div>
                                            </ActionMenuPortal>
                                        </div>
                                        <div className="card-body">
                                            <div className="card-row">
                                                <span className="card-label">Vendor:</span>
                                                <span className="card-value">{po.vendorName}</span>
                                            </div>
                                            <div className="card-row">
                                                <span className="card-label">Project:</span>
                                                <span className="card-value">{po.projectName}</span>
                                            </div>

                                            <div className="card-row">
                                                <span className="card-label">Till Date:</span>
                                                <span className="card-value">{formatDate(po.date)}</span>
                                            </div>
                                            <div className="card-row">
                                                <span className="card-label">Vendors:</span>
                                                <span className="card-value">
                                                    <span className="vendor-count-badge">{po.vendorCount}</span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className={isMobile ? "pagination-card" : ""}>
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                totalRecords={totalRecords}
                                currentCount={displayedPOs.length}
                                entityName="View Vendor Quotation"
                             limit={limit} onLimitChange={handleLimitChange} />
                        </div>
                    </>
                )}
            </div>
        </div>

        {/* WhatsApp Share Modal */}
        {showWhatsappModal && (
            <div className="modal-overlay" onClick={() => setShowWhatsappModal(false)}>
                <div className="modal-content share-modal-content" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3>Share on Whatsapp</h3>
                        <button className="close-btn" onClick={() => setShowWhatsappModal(false)}>
                            <MdClose size={20} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <p className="share-instruction">
                            Select vendors to send the link via Whatsapp.
                        </p>

                        {loadingVendors ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                                <Loader text="Loading vendor details..." />
                            </div>
                        ) : selectedVendorsForWhatsapp.length === 0 ? (
                            <p className="no-data">No vendors found for this order.</p>
                        ) : (
                            <div className="vendor-links-list">
                                {selectedVendorsForWhatsapp.map((vendor) => (
                                    <div key={vendor.vendorId} className="vendor-link-item">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <input
                                                type="checkbox"
                                                id={`vendor-${vendor.vendorId}`}
                                                checked={vendor.isChecked}
                                                onChange={() => handleWhatsappCheckboxChange(vendor.vendorId)}
                                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                disabled={!vendor.mobileNumber}
                                            />
                                            <div className="vendor-info">
                                                <span className="vendor-name">{vendor.vendorName}</span>
                                                <span className="vendor-email">
                                                    {vendor.mobileNumber ? vendor.mobileNumber : 'No Number'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="form-actions">
                            <button
                                className="save-btn"
                                onClick={handleSendWhatsapp}
                                disabled={loadingVendors || sendingWhatsapp || selectedVendorsForWhatsapp.filter(v => v.isChecked).length === 0}
                            >
                                {sendingWhatsapp ? 'Sending...' : (
                                    <>
                                        <FaWhatsapp size={18} /> Send WhatsApp
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Add Vendor Modal */}
        <Modal
            isOpen={showAddVendorModal}
            onClose={() => setShowAddVendorModal(false)}
            title="Add Vendor to Request"
            width="500px"
            footer={
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', width: '100%' }}>
                    <button
                        className="swal-cancel-btn"
                        onClick={() => setShowAddVendorModal(false)}
                        style={{ margin: 0 }}
                    >
                        Cancel
                    </button>
                    <button
                        className="swal-confirm-btn"
                        onClick={handleAddVendorSubmit}
                        disabled={loadingRemainingVendors || addingVendor || !selectedVendorForAdd}
                        style={{ margin: 0, background: '#10b981' }}
                    >
                        {addingVendor ? 'Adding...' : 'Add Vendor'}
                    </button>
                </div>
            }
        >
            <div style={{ padding: '10px 0' }}>
                <p style={{ marginBottom: '15px', color: '#6b7280', fontSize: '0.9rem' }}>
                    Select a vendor to add to Request <strong>#{selectedPoForAddVendor?.requestNumber?.toUpperCase()}</strong>.
                </p>
                <div className="form-group">
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Select Vendor</label>
                    <Select
                        options={remainingVendors}
                        value={selectedVendorForAdd}
                        onChange={setSelectedVendorForAdd}
                        isLoading={loadingRemainingVendors}
                        placeholder="Search and select vendor..."
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                            control: (base) => ({
                                ...base,
                                borderRadius: '8px',
                                padding: '2px',
                                borderColor: '#d1d5db',
                                '&:hover': {
                                    borderColor: '#4f46e5'
                                }
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 99999 })
                        }}
                    />
                </div>
            </div>
        </Modal>
        </>
    );
};

export default ViewVendorQuotations;
